import { Link } from "wouter";
import {
  BarChart3,
  Bell,
  Boxes,
  Calendar,
  Mic,
  Music,
  Network,
  Radio,
  Smartphone,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

export default function Landing() {
  const { status } = useAuth();
  const signedIn = status === "signed-in";
  return (
    <div className="min-h-dvh bg-background">
      <header
        className="flex h-14 items-center justify-between px-4 sm:px-8 border-b border-border bg-card/80 sticky top-0 z-30 backdrop-blur"
        style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
      >
        <Link href="/" aria-label="Cornholio">
          <img
            src="/logo-cornholio.png"
            alt="Cornholio"
            width={216}
            height={38}
            className="h-7 w-auto"
          />
        </Link>
        <div className="flex items-center gap-2">
          {signedIn ? (
            <Button asChild size="sm">
              <Link href="/dashboard">Open app</Link>
            </Button>
          ) : (
            <>
              <Link href="/sign-in" className="text-sm font-medium hover:underline">
                Sign in
              </Link>
              <Button asChild size="sm">
                <Link href="/sign-up">Get started</Link>
              </Button>
            </>
          )}
        </div>
      </header>

      <section className="px-4 sm:px-8 py-16 sm:py-24 text-center max-w-3xl mx-auto">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[11px] font-display uppercase tracking-widest text-primary">
          <Mic size={12} /> Now with a live AI announcer
        </span>
        <h1 className="mt-5 text-4xl sm:text-6xl font-display uppercase tracking-tight leading-[1.05]">
          The Best Way to <br className="hidden sm:inline" />
          <span className="text-primary">Play Cornhole.</span>
        </h1>
        <p className="text-base sm:text-xl text-muted-foreground mt-5 sm:mt-7 max-w-2xl mx-auto leading-relaxed">
          The complete platform for competitive cornhole — from league night
          to semi-pro. Score live in a tap with a real AI announcer calling the
          action, sharpen up in solo practice, or run full 3D virtual matches.
          Deep stats, tournaments, and spectator links come standard.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          {signedIn ? (
            <Button asChild size="lg">
              <Link href="/dashboard">Open the app</Link>
            </Button>
          ) : (
            <>
              <Button asChild size="lg">
                <Link href="/sign-up">Start your league</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link href="/sign-in">I have an account</Link>
              </Button>
            </>
          )}
        </div>
      </section>

      <section className="bg-card border-y border-border py-12 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <h2 className="text-center text-xs font-display uppercase tracking-[0.3em] text-muted-foreground mb-10">
            What's in the box
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
            <Feature
              Icon={Zap}
              title="Fast, accurate scoring"
              body="Enter each side's points for the round and the app cancels for the true net — the way real cornhole is scored. Edit any round mid-game. Sound + haptics, a big 4-bagger fanfare, and a 'throwing first' cue."
            />
            <Feature
              Icon={Mic}
              highlight
              title="Live AI announcer"
              body="A commentator calls your game out loud in smack-talk, comedy, or full savage mode — working in the real score and stats, and popping off on 4-baggers, lead changes, and match point."
            />
            <Feature
              Icon={BarChart3}
              title="Deep stats & leaderboards"
              body="Win %, points-per-round, offense vs. defense, head-to-head, best rounds, and org-wide insights. Every completed game feeds the leaderboard automatically."
            />
            <Feature
              Icon={Trophy}
              title="Best-of-3 championships"
              body="Rematch the same crew and the app tracks the series — the deciding win triggers a full-screen champion salute. No setup, it just knows."
            />
            <Feature
              Icon={Network}
              title="Tournaments + brackets"
              body="Single elim, round robin, multi-board concurrent play. The bracket viewer auto-advances as games finish."
            />
            <Feature
              Icon={Radio}
              title="Live spectator links"
              body="Every game gets a /g/<code> share link — no account required to watch. Real-time score over SSE, with rich link previews for iMessage and Slack."
            />
            <Feature
              Icon={Boxes}
              title="Practice & virtual 3D play"
              body="A full 3D court with real physics — bag arc, board slope, slide-to-hole. Swipe to throw on your phone. Drill solo, sharpen up against a Ghost AI, or face a friend in AI-designed arenas."
            />
            <Feature
              Icon={Music}
              title="Background music"
              body="Bring a playlist for the boards — it ducks itself under every announcer call and swells back between them, so the vibe never fights the commentary."
            />
            <Feature
              Icon={Calendar}
              title="Events + public pages"
              body="Org event listings, player registration, and public /events discovery with geo + JSON-LD so search engines pick it up."
            />
            <Feature
              Icon={Users}
              title="One account, every roster"
              body="Backyard league, sports-bar circuit, work team — one login, every roster you're on. Invite by email; mutual-consent claim for existing players."
            />
            <Feature
              Icon={Bell}
              title="Push notifications"
              body="Web push when your match is up and when your game finishes — real VAPID delivery that works on installed home-screen apps."
            />
            <Feature
              Icon={Smartphone}
              title="Installs like an app"
              body="A PWA with a home-screen icon, offline shell, and instant auto-update. Works on phones, tablets, and desktops — no app store required."
            />
          </div>
        </div>
      </section>

      <footer className="py-10 text-center text-sm text-muted-foreground font-display uppercase tracking-widest">
        Cornholio · a killerapps.dev product
      </footer>
    </div>
  );
}

function Feature({
  Icon,
  title,
  body,
  highlight,
}: {
  Icon: typeof Trophy;
  title: string;
  body: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={
        highlight
          ? "space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-5 -m-1"
          : "space-y-3"
      }
    >
      <div
        className={`h-12 w-12 rounded-lg flex items-center justify-center ${
          highlight
            ? "bg-primary text-primary-foreground"
            : "bg-primary/10 text-primary"
        }`}
      >
        <Icon size={26} />
      </div>
      <h3 className="font-display uppercase text-xl tracking-tight flex items-center gap-2">
        {title}
        {highlight ? (
          <span className="text-[9px] font-display uppercase tracking-widest text-primary bg-primary/10 border border-primary/30 rounded-full px-1.5 py-0.5">
            New
          </span>
        ) : null}
      </h3>
      <p className="text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}
